'use client';

import { useCallback, useEffect, useState } from 'react';
import { getAuthToken } from '@/lib/auth/token';

/** One row of GET /api/org/brands. */
export interface BrandRow {
  id: string;
  name: string;
  logoUrl: string | null;
  archivedAt: string | null;
  activeCampaigns: number;
  viewers: number;
  createdAt: string;
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Every mounted useBrands() refetches on this event, so the sidebar filter and
 * the pill on a card pick up a brand created / archived on another screen
 * without a reload (client-side navigation keeps them mounted).
 */
const CHANGED_EVENT = 'cannect:brands-changed';
const notifyBrandsChanged = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHANGED_EVENT));
};

/**
 * The agency's brands (docs/ORG_GROUPS_PLAN.md §6). `includeArchived` is for
 * the management page; pickers and filters only want live brands.
 */
export function useBrands(opts: { includeArchived?: boolean } = {}) {
  const [brands, setBrands] = useState<BrandRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/org/brands${opts.includeArchived ? '?includeArchived=1' : ''}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(String(res.status));
      const json = await res.json();
      setBrands(json.brands ?? []);
      setError(null);
    } catch (e) {
      setBrands((prev) => prev ?? []);
      setError(e instanceof Error ? e.message : 'failed');
    }
  }, [opts.includeArchived]);

  useEffect(() => {
    void refresh();
    const onChanged = () => void refresh();
    window.addEventListener(CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CHANGED_EVENT, onChanged);
  }, [refresh]);

  return { brands, error, refresh };
}

export type BrandMutationResult = { ok: true; brand: BrandRow } | { ok: false; error: string; status: number };

async function mutate(path: string, method: 'POST' | 'PATCH', body: unknown): Promise<BrandMutationResult> {
  const res = await fetch(path, { method, headers: authHeaders(), body: JSON.stringify(body) });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: json?.error ?? 'failed', status: res.status };
  notifyBrandsChanged();
  return { ok: true, brand: json.brand };
}

export const createBrand = (name: string) => mutate('/api/org/brands', 'POST', { name });
export const renameBrand = (id: string, name: string) => mutate(`/api/org/brands/${id}`, 'PATCH', { name });
export const archiveBrand = (id: string, archive: boolean) =>
  mutate(`/api/org/brands/${id}`, 'PATCH', { action: archive ? 'archive' : 'unarchive' });
